import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最多阅读' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [showFilters, setShowFilters] = useState(false);

  const category = categories.find((c) => c.slug === categorySlug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  let filteredComics = comics.filter((c) => c.category === category.id);

  switch (sortBy) {
    case 'popular':
      filteredComics = [...filteredComics].sort(
        (a, b) => parseInt(b.views.replace('万', '0000')) - parseInt(a.views.replace('万', '0000'))
      );
      break;
    case 'rating':
      filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
      break;
    case 'latest':
    default:
      filteredComics = [...filteredComics].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${category.name}漫画` },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩国漫画 - 尾巴漫画网`,
    description: `${category.description}，尾巴漫画网为您精选${category.name}类韩国漫画，高清无删减在线免费阅读。`,
    url: `https://www.weibamanhua.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '尾巴漫画网',
      url: 'https://www.weibamanhua.com',
    },
  };

  return (
    <>
      <SEOMeta
        title={`${category.name}韩国漫画_${category.name}韩漫在线免费阅读`}
        description={`${category.description}，尾巴漫画网为您精选${category.name}类韩国漫画，高清无删减在线免费阅读。海量${category.name}韩漫资源，每日更新。`}
        keywords={`${category.name}漫画,${category.name}韩漫,韩国漫画${category.name},尾巴漫画网,韩漫在线免费阅读`}
        canonicalUrl={`https://www.weibamanhua.com/category/${category.slug}`}
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${category.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}韩国漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                      cat.slug === categorySlug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200/70 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序:</span>
                <div className="flex bg-background-50 rounded-full p-0.5 border border-background-200/70">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap ${
                        sortBy === opt.value
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 md:gap-6">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}